import { useRouter } from 'next/router'
import 'react-day-picker/dist/style.css'
import React, {
  useCallback,
  useEffect,
  useState,
  Fragment,
  useRef,
} from 'react'

import { ImageWithPlaceholder } from '@components/ImageWithPlaceholder'
import { searchRoute } from '@helpers/searchRoute'
import { useMediaQuery } from '@hooks/use-media-query'
import BurgerIcon from '@public/images/burger-menu.svg'
import CrossIcon from '@public/images/cross.svg'
import LogoBlack from '@public/images/logo-black.svg'
import Logo from '@public/images/logo.svg'
import { NormalizedWrapper } from '@theme'
import { Link } from '@ui/links/Link'

import { config } from './config'
import { PopupFirst } from './PopupFirst'
import { PopupForm } from './PopupForm'
import {
  Wrapper,
  List,
  ListItem,
  BurgerMenuButton,
  LogoWrapper,
  PopUpWindow,
  Content,
  ContentContainer,
  PopUpSectionWrapper,
  LinkButton,
  Links,
  AdaptiveContent,
  Container,
  Block,
  AdditionalPathWrapper,
  AdditionalPathContent,
  AdaptiveSectionContent,
  AdaptiveSectionHeading,
  AdditionalList,
  AdditionalContent,
  SocialList,
  SocialListItem,
  FooterUi,
  Symbol,
  ContentWrapper,
  ButtonContainer,
  ButtonBookCall,
  LinkToFooterWrapper,
  PopupDescription,
  LinkWrapper,
  HandsetIcon,
  AdditionalInfoSubtitle,
} from './styles'

export function Header() {
  const [open, setOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  const [openFirstLevel, setOpenFirstLevel] = useState('')
  const [openSecondLevel, setOpenSecondLevel] = useState('')
  const [close, setClose] = useState(false)
  const [modalBookCall, setModalBookCall] = useState(1)

  const router = useRouter()

  const isDesktop = useMediaQuery('(max-width: 1024px)')

  const toggleVisibility = () => {
    window.pageYOffset > 1 ? setIsVisible(true) : setIsVisible(false)
  }

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility)
    return () => {
      window.removeEventListener('scroll', toggleVisibility)
    }
  }, [])

  useEffect(() => {
    if (open) {
      document.body.style.overflowY = 'hidden'
    } else {
      document.body.style.overflowY = 'auto'
    }
  }, [open])

  const handleClose = () => {
    setClose(false)
  }

  const handleOnChangeButton = useCallback(() => {
    setOpen(value => !value)
    if (close) handleClose()
  }, [close])

  const handleClick = useCallback(() => {
    router.push('/')
  }, [router])

  const navigateTo = useCallback(
    link => event => {
      event.preventDefault()
      setOpen(false)
      setOpenFirstLevel('')
      setOpenSecondLevel('')
      router.push(link)
    },
    [router]
  )

  const handleOpenFirstLevel = (label, path) => event => {
    if (path) {
      setOpen(false)
      return null
    }
    event.preventDefault()
    setOpenFirstLevel(prev => (prev === label ? '' : label))
    setOpenSecondLevel('')
  }

  const handleOpenSecondLevel = label => event => {
    event.preventDefault()
    setOpenSecondLevel(prev => (prev === label ? '' : label))
  }

  const handlePopup = () => {
    setModalBookCall(1)
    setClose(true)
  }

  const handleNextFirst = () => {
    setModalBookCall(2)
    setClose(true)
  }
  const handleNextSecond = () => {
    setClose(true)
  }

  const handleBookCallOnKeyDown = event => {
    const { key } = event
    if (key === 'Enter') handlePopup()
  }

  const bookCall = () => {
    if (modalBookCall === 1) {
      return (
        <PopupFirst
          onClick={handleNextFirst}
          modalBookCall={modalBookCall}
          handleClose={handleClose}
        />
      )
    }
    if (modalBookCall === 2) {
      return (
        <PopupForm
          modalBookCall={modalBookCall}
          handleClose={handleClose}
          handleBack={handlePopup}
          handleThird={handleNextSecond}
        />
      )
    }
  }

  const removeFocus = () => {
    document.activeElement.blur()
    handleClose()
  }

  const [isIOSDevice, setIsIOSDevice] = useState(false)

  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setIsIOSDevice(isIOS)
  }, [])

  const listRef = useRef(null)
  const containerRef = useRef(null)

  const initialListScrollRef = useRef(true)

  const handleScrollContainer = () => {
    if (!isIOSDevice) return

    const listNode = listRef.current

    if (!listNode) return

    if (listNode.style.overflowY !== 'auto') {
      listNode.style.overflowY = 'auto'
    }
  }

  const handleScrollList = () => {
    if (!isIOSDevice) return

    const listNode = listRef.current
    const containerNode = containerRef.current

    if (!listNode || !containerNode) return

    const initialScroll = initialListScrollRef.current

    if (initialScroll && listNode.scrollTop !== 0) {
      initialListScrollRef.current = false
    }

    const isAtBottom =
      listNode.scrollHeight - listNode.scrollTop === listNode.clientHeight

    const scrolledToTop = !initialScroll && listNode.scrollTop === 0

    const isContainerAtTop = containerNode.scrollTop === 0

    if (isAtBottom || (scrolledToTop && !isContainerAtTop)) {
      listNode.style.overflowY = 'hidden'
    }
  }

  return (
    <Container
      ref={containerRef}
      isVisible={isVisible}
      open={open && isDesktop}
      onScroll={handleScrollContainer}
    >
      <NormalizedWrapper paddingValue="0">
        <Wrapper id="header" open={open} isVisible={isVisible}>
          <LogoWrapper>
            <ButtonContainer
              aria-label="Logo Button"
              data-cy="logo button"
              onClick={handleClick}
            >
              {open && isDesktop ? (
                <LogoBlack alt="Modsen Logo" />
              ) : (
                <Logo alt="Modsen Logo" />
              )}
            </ButtonContainer>
            <BurgerMenuButton
              data-cy="burger menu"
              aria-label="Burger Menu Button"
              open={open}
              onClick={handleOnChangeButton}
            >
              {open ? <CrossIcon /> : <BurgerIcon />}
            </BurgerMenuButton>
          </LogoWrapper>

          <List ref={listRef} onScroll={handleScrollList} open={open}>
            {config.links.map(
              ({
                id,
                path,
                label,
                info,
                sections,
                sectionsAdaptive,
                description,
                popUpHeight,
                popUpWidth,
                popUpLeft,
                additionalPaths,
                hexagon,
                withPadding,
              }) => (
                <ListItem
                  key={id}
                  info={info}
                  open={openFirstLevel === label}
                  $active={
                    path === router.pathname ||
                    additionalPaths.includes(searchRoute(router.pathname))
                  }
                  onClick={removeFocus}
                >
                  <Block>
                    {!isDesktop ? (
                      <>
                        <Links
                          $active={
                            path === router.pathname ||
                            additionalPaths.includes(
                              searchRoute(router.pathname)
                            )
                          }
                          info={info}
                          data-cy={id}
                          href={path}
                          title={`Go to ${label}`}
                          color="milkyWhite"
                          withPadding={withPadding}
                        >
                          {label}
                        </Links>
                        {info && (
                          <PopUpWindow
                            className="submenu"
                            height={popUpHeight}
                            width={popUpWidth}
                            left={popUpLeft}
                          >
                            {sections?.map(
                              ({ sectionTitle, sectionValues }) => (
                                <PopUpSectionWrapper
                                  key={sectionTitle}
                                  hexagon={hexagon}
                                >
                                  {sectionTitle && <p>{sectionTitle}</p>}
                                  {sectionValues.map(
                                    ({
                                      value,
                                      link,
                                      additionalRoute,
                                      additionalInfo,
                                    }) => (
                                      <ContentContainer
                                        key={value}
                                        info={additionalInfo}
                                      >
                                        <LinkWrapper>
                                          <Content
                                            $active={
                                              link === router.pathname
                                                ? link
                                                : undefined
                                            }
                                            href={link}
                                            onMouseDown={e => {
                                              e.preventDefault()
                                            }}
                                          >
                                            {value}
                                          </Content>
                                          {additionalInfo && (
                                            <div className="addInfo">
                                              <p>{additionalInfo.title}</p>
                                              <AdditionalInfoSubtitle>
                                                {additionalInfo.subtitle}
                                              </AdditionalInfoSubtitle>
                                            </div>
                                          )}
                                        </LinkWrapper>
                                        {additionalRoute && (
                                          <AdditionalPathWrapper>
                                            <AdditionalPathContent className="additional-box">
                                              {additionalRoute.map(
                                                ({
                                                  value: routeValue,
                                                  link: routeLink,
                                                }) => (
                                                  <Content
                                                    $active={
                                                      routeLink ===
                                                      router.pathname
                                                        ? routeLink
                                                        : undefined
                                                    }
                                                    href={routeLink}
                                                    key={routeValue + routeLink}
                                                  >
                                                    {routeValue}
                                                  </Content>
                                                )
                                              )}
                                            </AdditionalPathContent>
                                          </AdditionalPathWrapper>
                                        )}
                                      </ContentContainer>
                                    )
                                  )}
                                </PopUpSectionWrapper>
                              )
                            )}
                            {hexagon ||
                              (description && (
                                <PopupDescription>
                                  <p>{description.title}</p>
                                  <AdditionalInfoSubtitle>
                                    {description.subtitle}
                                  </AdditionalInfoSubtitle>
                                </PopupDescription>
                              ))}
                          </PopUpWindow>
                        )}
                      </>
                    ) : (
                      <>
                        <Links
                          $active={
                            path === router.pathname ||
                            additionalPaths.includes(
                              searchRoute(router.pathname)
                            )
                          }
                          href={path}
                          data-cy={id}
                          title={`Go to ${label}`}
                          info={info}
                          color="secondary"
                          onClick={handleOpenFirstLevel(label, path)}
                          $sections={!!sections}
                          open={openFirstLevel === label}
                        >
                          {label}
                        </Links>
                        {sections && (
                          <AdaptiveContent open={openFirstLevel === label}>
                            {sectionsAdaptive?.map(({ title, values }) => (
                              <AdaptiveSectionContent
                                className="adaptive-section"
                                key={title}
                              >
                                <AdaptiveSectionHeading>
                                  {title}
                                </AdaptiveSectionHeading>
                                {values.map(
                                  ({
                                    value,
                                    additionalRoute,
                                    link,
                                    width,
                                    height,
                                    alt,
                                    src,
                                    subValue,
                                  }) => (
                                    <Fragment key={value}>
                                      <ContentWrapper column={additionalRoute}>
                                        <Content
                                          className="content"
                                          $active={
                                            link === router.pathname
                                              ? link
                                              : undefined
                                          }
                                          onClick={navigateTo(link)}
                                        >
                                          {value}
                                          <ImageWithPlaceholder
                                            width={width}
                                            height={height}
                                            alt={alt}
                                            src={src}
                                          />
                                        </Content>
                                        {subValue && <p>{subValue}</p>}
                                        {additionalRoute && (
                                          <Symbol
                                            onClick={handleOpenSecondLevel(
                                              value
                                            )}
                                            open={openSecondLevel === value}
                                          >
                                            ➜
                                          </Symbol>
                                        )}
                                      </ContentWrapper>
                                      {additionalRoute && (
                                        <AdditionalList
                                          open={openSecondLevel === value}
                                        >
                                          {additionalRoute.map(
                                            ({
                                              value: routeValue,
                                              link: routeLink,
                                            }) => (
                                              <AdditionalContent
                                                key={routeLink + routeValue}
                                              >
                                                <Content
                                                  $active={
                                                    routeLink ===
                                                    router.pathname
                                                      ? routeLink
                                                      : undefined
                                                  }
                                                  key={routeValue + routeLink}
                                                  onClick={navigateTo(
                                                    routeLink
                                                  )}
                                                >
                                                  {routeValue}
                                                </Content>
                                              </AdditionalContent>
                                            )
                                          )}
                                        </AdditionalList>
                                      )}
                                    </Fragment>
                                  )
                                )}
                              </AdaptiveSectionContent>
                            ))}
                          </AdaptiveContent>
                        )}
                      </>
                    )}
                  </Block>
                </ListItem>
              )
            )}
          </List>
          <LinkToFooterWrapper>
            <FooterUi>
              <LinkButton
                data-cy={config.buttonLink.id}
                onClick={handleOpenFirstLevel(
                  config.buttonLink.label,
                  config.buttonLink.path
                )}
                open={open && isDesktop}
                key={config.buttonLink.id}
                href={config.buttonLink.path}
                title={`Go to ${config.buttonLink.label}`}
              >
                {config.buttonLink.label}
              </LinkButton>
              <SocialList open={open && isDesktop}>
                {config.socialLinks.map(
                  ({ id, path, label, imagePath, imageWidth, imageHeight }) => (
                    <SocialListItem key={id}>
                      <Link
                        href={path}
                        title={`Go to ${label}`}
                        target="_blank"
                        rel="noopener"
                      >
                        <ImageWithPlaceholder
                          src={imagePath}
                          alt={label}
                          width={imageWidth}
                          height={imageHeight}
                        />
                      </Link>
                    </SocialListItem>
                  )
                )}
              </SocialList>
            </FooterUi>
            <FooterUi>
              <ButtonBookCall
                id="book-call-btn"
                onClick={close ? handleClose : handlePopup}
                onKeyDown={handleBookCallOnKeyDown}
                open={open}
                title={config.buttonBookCall.title}
                tabIndex={0}
              >
                <HandsetIcon alt="make-call" className="book-call" />
                Book a call
              </ButtonBookCall>
              {close && bookCall()}
            </FooterUi>
          </LinkToFooterWrapper>
        </Wrapper>
      </NormalizedWrapper>
    </Container>
  )
}
